import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, forkJoin, from, tap, merge, withLatestFrom, filter } from 'rxjs';

import { AnalyticsService } from 'src/app/core/analytics/analytics.service';
import { ParticipantStep } from 'src/app/core/participant-steps/enums/participant-step.enum';
import { ESignHubService } from 'src/app/core/signalR/esign-hub.service';
import { ERROR_MESSAGES } from '../../constants/error-messages';
import { SessionCancellationReason } from '../../models/session-cancellation-reason.enum';
import { ParticipantStepStateService } from 'src/app/core/participant-steps/services/participant-step-state.service';
import { ParticipantService } from '../../services/participant.service';
import { SessionInitializerService } from '../../services/session-initializer.service';
import {
  loadSession,
  loadSessionSuccess,
  loadSessionFailure,
  initializeSession,
  initializeSessionSuccess,
  initializeSessionFailure,
  connectToSignalRHub,
  connectToSignalRHubSuccess,
  connectToSignalRHubFailure,
  receiveSigningStatusUpdate,
  receiveParticipantStatusUpdate,
  flagSessionCanceled,
} from '../actions/session.actions';
import { selectParticipantId } from '../selectors/session.selectors';
import { SigningService } from '../../services/signing.service';

@Injectable()
export class SessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly eSignHubService = inject(ESignHubService);
  private readonly participantService = inject(ParticipantService);
  private readonly signingService = inject(SigningService);
  private readonly participantStepStateService = inject(
    ParticipantStepStateService,
  );
  private readonly sessionInitializerService = inject(
    SessionInitializerService,
  );
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  loadSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSession),
      switchMap(({ participantId }) =>
        forkJoin({
          participant: this.participantService.getById(participantId),
          signing: this.signingService.getByParticipantId(participantId),
        }).pipe(
          tap(({ participant, signing }) => {
            this.analyticsService.identifyUser(participant.id, {
              esign_participant_id: participant.id,
              esign_signing_id: signing.id,
            });
          }),
          switchMap(({ participant, signing }) => [
            loadSessionSuccess({ participant, signing }),
            initializeSession({ participantId, signingId: signing.id }),
            connectToSignalRHub(),
          ]),
          catchError((error) => {
            return of(
              loadSessionFailure({
                error: error.message || 'Failed to load session',
              }),
            );
          }),
        ),
      ),
    ),
  );

  initializeSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initializeSession),
      switchMap(({ participantId, signingId }) =>
        from(
          this.sessionInitializerService.initializeSession(
            participantId,
            signingId,
          ),
        ).pipe(
          tap((sessionId) => {
            this.analyticsService.identifyUser(participantId, {
              esign_session_id: sessionId,
            });
          }),
          map((sessionId) => initializeSessionSuccess({ sessionId })),
          catchError((error) =>
            of(
              initializeSessionFailure({
                error: error.message || ERROR_MESSAGES.SESSION_CREATION_FAILED,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  connectToSignalRHub$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectToSignalRHub),
      switchMap(() =>
        from(this.eSignHubService.connectAsync()).pipe(
          switchMap(() =>
            merge(
              of(connectToSignalRHubSuccess()),
              this.eSignHubService.signingStatusUpdate$.pipe(
                map((update) => receiveSigningStatusUpdate({ update })),
              ),
              this.eSignHubService.participantStatusUpdate$.pipe(
                map((update) => receiveParticipantStatusUpdate({ update })),
              ),
            ),
          ),
          catchError((error) =>
            of(
              connectToSignalRHubFailure({
                error: error.message || 'Failed to connect to SignalR hub',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  participantKbaFailureDetection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(receiveParticipantStatusUpdate),
      withLatestFrom(this.store.select(selectParticipantId)),
      filter(
        ([{ update }, currentParticipantId]) =>
          update.currentStep === ParticipantStep.KnowledgeBasedAuth &&
          update.stepStatus === 'Failed' &&
          !!currentParticipantId &&
          update.participantId !== currentParticipantId,
      ),
      map(() =>
        flagSessionCanceled({
          reason: SessionCancellationReason.OtherParticipantKbaFailed,
        }),
      ),
    ),
  );

  navigateOnSessionCanceled$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(flagSessionCanceled),
        tap(() => {
          this.router.navigate(['/session-canceled']);
        }),
      ),
    { dispatch: false },
  );
}
